/*
 * Collection Team Filter
 *
 * Upgrades the server-rendered tile row into part of the facets AJAX flow, and
 * runs the row as a Swiper carousel.
 *
 * Every tile ships from Liquid as a real link to the filtered collection URL, so
 * the row still works with JavaScript off. Here we intercept the click and hand
 * the query string to `facets.js` instead, which swaps the product grid and
 * re-renders every filter group in the drawer - that is how a tile and its
 * drawer row stay in agreement.
 *
 * The reverse direction cannot ride on the section fetch, because this row is
 * its own section and is never in the fetched HTML. `facets.js` fires
 * `facets:updated` on `document` with the new query string, and the row re-derives
 * its own state from it: which tiles are active, and what each tile's href should
 * toggle to next. That also covers back/forward, since `popstate` routes through
 * the same `renderPage`.
 *
 * In Team Links mode no tile carries `data-filter-name` and there is no See all
 * button, so everything below no-ops and only the carousel runs.
 *
 * FacetFiltersForm is a top-level `class` in a classic script, so it is a global
 * lexical binding rather than a property of `window` - it has to be tested with
 * `typeof`, not `window.FacetFiltersForm`.
 */

class CollectionTeamFilter extends HTMLElement {
  constructor() {
    super();

    this.onClick = this.onClick.bind(this);
    this.onFacetsUpdated = this.onFacetsUpdated.bind(this);
  }

  connectedCallback() {
    this.filterLabel = this.dataset.filterLabel;
    this.tiles = Array.from(this.querySelectorAll('[data-team-tile]'));
    this.slider = this.querySelector('.collection-team-filter__slider');

    this.initSlider();

    this.addEventListener('click', this.onClick);
    document.addEventListener('facets:updated', this.onFacetsUpdated);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('facets:updated', this.onFacetsUpdated);

    if (this.swiper) this.swiper.destroy(true, true);
  }

  /* Mirrors anchored-collections.js, except the gaps are section settings and
     arrive on data attributes. Elements rather than selector strings: the
     theme's button classes are not unique and several sliders share a page. */
  initSlider() {
    if (!this.slider || typeof Swiper === 'undefined') return;

    const prevEl = this.slider.querySelector('.swiper-button--prev');
    const nextEl = this.slider.querySelector('.swiper-button--next');
    const spaceBetween = parseFloat(this.slider.dataset.spaceMobile) || 12;
    const spaceDesktop = parseFloat(this.slider.dataset.spaceDesktop) || 20;

    this.swiper = new Swiper(this.slider, {
      a11y: false,
      threshold: 3,
      slidesPerView: 'auto',
      spaceBetween,
      resistanceRatio: 0.72,
      navigation: { prevEl, nextEl },
      breakpoints: {
        990: { spaceBetween: spaceDesktop }
      }
    });

    if (prevEl) prevEl.setAttribute('aria-label', 'Previous teams');
    if (nextEl) nextEl.setAttribute('aria-label', 'Next teams');

    /* A team selected before the page loaded can sit well past the visible
       edge. */
    const active = this.querySelector('.collection-team-filter__tile.is-active');
    if (active) this.slideIntoView(active);
  }

  onClick(event) {
    const tile = event.target.closest('[data-team-tile]');

    if (tile) {
      this.onTileClick(event, tile);
      return;
    }

    if (event.target.closest('[data-see-all]')) {
      event.preventDefault();
      this.openFilterDrawer();
    }
  }

  /* A tile with no filter name has nothing to toggle - it is a plain team link.
     Let the browser follow it. */
  onTileClick(event, tile) {
    if (!tile.dataset.filterName) return;
    if (typeof FacetFiltersForm === 'undefined') return;
    if (!document.querySelector('facet-filters-form')) return;

    event.preventDefault();

    const search = new URL(tile.href, window.location.origin).search.slice(1);

    FacetFiltersForm.renderPage(search);
  }

  onFacetsUpdated(event) {
    this.sync(event.detail?.searchParams ?? window.location.search.slice(1));
  }

  sync(searchParams) {
    const params = new URLSearchParams(searchParams);

    this.tiles.forEach(tile => {
      const name = tile.dataset.filterName;
      const value = tile.dataset.filterValue;
      const isActive = Boolean(name) && params.getAll(name).includes(value);
      const wasActive = tile.classList.contains('is-active');

      tile.classList.toggle('is-active', isActive);

      if (isActive) {
        tile.setAttribute('aria-current', 'true');
      } else {
        tile.removeAttribute('aria-current');
      }

      /* Picked from the drawer, the tile can be off the visible edge of the
         carousel - a selection nobody can see. */
      if (isActive && !wasActive) this.slideIntoView(tile);

      tile.href = this.hrefFor(tile, params);
    });
  }

  slideIntoView(tile) {
    if (!this.swiper) return;

    const slide = tile.closest('.collection-team-filter__item');
    if (!slide) return;

    const viewport = this.slider.getBoundingClientRect();
    const rect = slide.getBoundingClientRect();
    if (rect.left >= viewport.left && rect.right <= viewport.right) return;

    /* Swiper moves the row by transform, so scrollLeft means nothing here. */
    const index = Array.from(this.swiper.slides).indexOf(slide);
    if (index > -1) this.swiper.slideTo(index);
  }

  /* Rebuild what this tile would toggle to, from the live selection. Mirrors what
     Shopify's own url_to_add / url_to_remove produce: the filter is multi-select,
     so other teams stay put, and `page` is dropped because the result set changed. */
  hrefFor(tile, params) {
    const name = tile.dataset.filterName;
    const value = tile.dataset.filterValue;

    if (!name) return tile.href;

    const next = new URLSearchParams(params);
    const current = next.getAll(name);
    const remaining = current.includes(value)
      ? current.filter(entry => entry !== value)
      : [...current, value];

    next.delete(name);
    remaining.forEach(entry => next.append(name, entry));
    next.delete('page');

    const query = next.toString();

    return `${window.location.pathname}${query ? `?${query}` : ''}`;
  }

  openFilterDrawer() {
    const drawer = document.querySelector('menu-drawer.facets__drawer');

    if (drawer) {
      const details = drawer.querySelector('details');
      const summary = drawer.querySelector('summary');

      if (summary && !details.classList.contains('menu-opening')) {
        summary.click();
      }

      /* One frame so the drawer has its `open` attribute before AccordionDefault
         measures scrollHeight for the expand transition. */
      requestAnimationFrame(() => this.openFilterGroup(drawer));
      return;
    }

    /* Sidebar filter style - there is no drawer to open, so just expand the group
       in place and put it on screen. */
    const aside = document.getElementById('FacetFiltersFormAside');
    if (!aside) return;

    this.openFilterGroup(aside);
    aside
      .querySelector(`details[data-filter-label="${this.filterLabel}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  openFilterGroup(root) {
    const group = root.querySelector(
      `details[data-filter-label="${this.filterLabel}"]`
    );

    /* AccordionDefault toggles, so clicking an already-open group would close it. */
    if (!group || group.classList.contains('is-active')) return;

    group.querySelector('summary')?.click();
  }
}

customElements.define('collection-team-filter', CollectionTeamFilter);
